import { Node, Rect, getBoundsOfRects } from "reactflow"
import {
  GROUP_MARGIN_HEIGHT,
  GROUP_MARGIN_WIDTH,
  GROUP_TEXT_HEIGHT_PADDING,
  GraphConverter,
  NODE_DEFAULT_HEIGHT,
  NODE_DEFAULT_WIDTH,
} from "@store/services/converter/graphConverter"
import { GraphNodeData, GraphNodeEdgeData, GroupNodeData } from "@store/services/converter/types"
import { GraphStoreType } from "@features/graph/Graph"
import { deepCopy } from "@features/graph/utils/graphUtils"

/**
 * @return true when group is expanded
 */
export const graphGroupCollapseExpand = (
  data: GroupNodeData,
  graphData: GraphNodeEdgeData,
  flowStore: GraphStoreType,
): boolean => {
  if (data.group.isCollapsed) {
    expandGroupNode(data, graphData, flowStore)
    return true
  }
  collapseGroupNode(data, graphData, flowStore)
  return false
}

const collapseGroupNode = (
  groupData: GroupNodeData,
  graphData: GraphNodeEdgeData,
  flowStore: GraphStoreType,
) => {
  const nestedGroupIds = getNestedGroupIds(groupData.id, graphData)
  const { nodeInternals } = flowStore.getState()
  const groupChildren = graphData.childrenGroupMap[groupData.id] || []
  for (const [id, node] of nodeInternals) {
    if (groupChildren.includes(id)) {
      nodeInternals.delete(id)
    } else if (groupData.group.nodes.includes(id)) {
      nodeInternals.delete(id)
    } else if (nestedGroupIds.has(id)) {
      nodeInternals.delete(id)
    } else if (groupData.id === id) {
      const defaultData = graphData.groupMap[groupData.id]
      node.data.group.isCollapsed = true
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      const size = GraphConverter.getGroupTextSize(`Group ${defaultData.group.label}`)
      node.style!.width = size.width
      node.style!.height = size.height + GROUP_TEXT_HEIGHT_PADDING
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      node.position.x = Math.round(defaultData.position.x)
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      node.position.y = Math.round(defaultData.position.y)
    }
  }

  // edges
  const edges = GraphConverter.convertEdges(graphData.zIndex, graphData.nodeMap, nodeInternals)

  // set state
  flowStore.setState({ nodeInternals, edges })
}

export const expandGroupNode = (
  groupData: GroupNodeData,
  graphData: GraphNodeEdgeData,
  flowStore: GraphStoreType,
) => {
  const nestedGroupIds = getNestedGroupIds(groupData.id, graphData)
  const nestedNodeIds = getNestedGroupNodeIds(groupData.id, nestedGroupIds, graphData)
  const currentGroupNodes = groupData.group.nodes.filter((n) => !nestedNodeIds.has(n))

  const { nodeInternals } = flowStore.getState()
  const rects: Rect[] = []

  // children group nodes
  const childrenGroups = graphData.childrenGroupMap[groupData.id]
  if (childrenGroups) {
    childrenGroups.forEach((groupId) => {
      const oldNodeInternals: Node<GroupNodeData> = nodeInternals.get(
        groupId,
      ) as Node<GroupNodeData>
      const data = structuredClone(graphData.groupMap[groupId])
      if (!data) return

      if (
        oldNodeInternals?.data?.group?.isCollapsed === false &&
        data?.group?.isCollapsed === true
      ) {
        nodeInternals.set(groupId, oldNodeInternals)
        return
      }

      const size = GraphConverter.getGroupTextSize(`Group ${data.group.label}`)
      rects.push({ ...data.position, ...size })
      const groupNode: Node<GroupNodeData> = {
        id: data.id,
        type: "group",
        style: {
          backgroundColor: data.group.color,
          color: "#000",
          width: size.width,
          height: size.height + GROUP_TEXT_HEIGHT_PADDING,
        },
        data,
        className: "light",
        position: {
          x: Math.round(data.position.x),
          y: Math.round(data.position.y),
        },
        zIndex: data.zIndex,
      }
      nodeInternals.set(groupId, groupNode)
    })
  }

  // nodes inside expanded group
  currentGroupNodes.forEach((nodeId) => {
    const data = deepCopy(graphData.nodeMap[nodeId])
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    rects.push({ ...data.position, width: NODE_DEFAULT_WIDTH, height: NODE_DEFAULT_HEIGHT })
    const singleNode: Node<GraphNodeData> = {
      id: nodeId,
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      type: data.type,
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      data,
      position: {
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        x: Math.round(data.position.x),
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        y: Math.round(data.position.y),
      },
      style: {
        width: NODE_DEFAULT_WIDTH,
        height: NODE_DEFAULT_HEIGHT,
      },
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      zIndex: data.zIndex,
    }
    nodeInternals.set(nodeId, singleNode)
  })

  // expanded group node
  const internalGroupData = nodeInternals.get(groupData.id)
  const rect = calculateGroupRect(
    groupData,
    { ...groupData.position, width: NODE_DEFAULT_WIDTH, height: NODE_DEFAULT_HEIGHT },
    rects,
  )
  if (internalGroupData) {
    internalGroupData.data.group.isCollapsed = false
    internalGroupData.style!.width = rect.width
    internalGroupData.style!.height = rect.height
    internalGroupData.position.x = rect.x
    internalGroupData.position.y = rect.y
  }

  // resize group node parent groups
  // probably need to get all parent nodes in correct order to resize everything, but it works for now
  const parentGroupIds = getParentGroupIds(groupData.id, graphData)
  let childRect = { ...rect }
  parentGroupIds.forEach((id) => {
    const node: Node<GroupNodeData> | undefined = nodeInternals.get(id)
    if (!node?.style) {
      return
    }
    const parentRect = {
      x: node.position.x,
      y: node.position.y,
      width: Number(node.style.width),
      height: Number(node.style.height),
    }
    childRect = resizeRectWithMargins(parentRect, childRect)
    node.position.x = childRect.x
    node.position.y = childRect.y
    node.style.width = childRect.width
    node.style.height = childRect.height
  })

  // edges
  const edges = GraphConverter.convertEdges(graphData.zIndex, graphData.nodeMap, nodeInternals)

  flowStore.setState({ nodeInternals, edges })
}

const getParentGroupIds = (groupId: string, graphData: GraphNodeEdgeData): string[] => {
  const out = []
  let group = graphData.groupMap[groupId]
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  while (group.group.parent) {
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    group = graphData.groupMap[group.group.parent]
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    out.push(group.id)
  }
  return out
}

const getNestedGroupIds = (groupId: string, graphData: GraphNodeEdgeData): Set<string> => {
  // returns set of nested group ids
  const out = new Set<string>()
  const temp = graphData.childrenGroupMap[groupId]?.concat()
  if (!temp) return out
  while (temp.length > 0) {
    const id = temp.pop()
    if (!id) break
    out.add(id)
    temp.push(...(graphData.childrenGroupMap[id]?.concat() || []))
  }
  return out
}

const getNestedGroupNodeIds = (
  groupId: string,
  nestedGroupIds: Set<string>,
  graphData: GraphNodeEdgeData,
): Set<string> => {
  // returns all nodes that are inside nested groups of groupId
  const out = new Set<string>()
  const groupNode = graphData.groupMap[groupId]
  if (!groupNode) return out
  nestedGroupIds.forEach((g) => {
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    graphData.groupMap[g].group.nodes.forEach((n) => out.add(n))
  })
  return out
}

const resizeRectWithMargins = (rect1: Rect, rect2: Rect): Rect => {
  // apply margins to second rect to get bound with margins
  rect2.x -= GROUP_MARGIN_WIDTH
  rect2.y -= GROUP_MARGIN_HEIGHT
  rect2.width += GROUP_MARGIN_WIDTH
  rect2.height += GROUP_MARGIN_HEIGHT
  let { x, y, width, height } = getBoundsOfRects(rect1, rect2)
  // apply margins
  if (x !== rect1.x || width !== rect1.width) {
    x -= GROUP_MARGIN_WIDTH
    width += GROUP_MARGIN_WIDTH * 2
  }
  if (y !== rect1.y || height !== rect1.height) {
    y -= GROUP_MARGIN_HEIGHT
    height += GROUP_MARGIN_HEIGHT * 2
  }
  return { x, y, width, height }
}

const calculateGroupRect = (groupData: GroupNodeData, parent: Rect, children: Rect[]): Rect => {
  // creates rectangle that is bounded by xy and points

  for (const child of children) {
    parent = getBoundsOfRects(parent, child)
  }

  return {
    x: Math.round(parent.x) - GROUP_MARGIN_WIDTH,
    y: Math.round(parent.y) - GROUP_MARGIN_HEIGHT,
    width: Math.round(parent.width) + (groupData.group.parent ? GROUP_MARGIN_WIDTH * 2 : 300),
    height: Math.round(parent.height) + GROUP_MARGIN_HEIGHT * 2,
  }
}
